<script setup lang="ts">
import { computed, ref } from 'vue';

import SmartFilters from './SmartFilters.vue';
import { SearchService as SharedSearchService } from '@/core/shared/search/search.service';
import ChevronDownIcon from '@/assets/svg/chevron-down.svg?component';

// The header owns query execution; this panel only stages pending filter state for the
// next explicit search. Collapsed by default; the toggle shows the active count so
// applied filters are visible without expanding.
const sharedSearchService = new SharedSearchService();
const smartFiltersRef = ref<InstanceType<typeof SmartFilters> | null>(null);
const open = ref(false);

// The service owns the count (same source as the sidebar header, which is suppressed
// here): Distance is a radius modifier of Location and isn't counted.
const activeCount = computed(() => sharedSearchService.activeSidebarFilterCount);

// Apply belongs to the filter form, so it lives inside the collapsed body on mobile too; the
// panel only relays it to the view, which owns search execution.
defineEmits<{ apply: [] }>();
</script>

<template>
  <div class="panel">
    <div class="bar">
      <button
        type="button"
        class="toggle"
        :aria-expanded="open"
        aria-controls="smart-filters-body"
        data-test="smart-filters-toggle"
        @click="open = !open"
      >
        <span>Filters</span>
        <span v-if="activeCount" class="count">{{ activeCount }}</span>
        <ChevronDownIcon class="chevron" :class="{ 'chevron--open': open }" viewBox="0 0 24 24" />
      </button>
      <button
        v-if="activeCount"
        type="button"
        class="clear"
        data-test="smart-filters-clear-all"
        @click="smartFiltersRef?.clearAll()"
      >
        Clear all
      </button>
    </div>
    <div v-show="open" id="smart-filters-body" class="body">
      <SmartFilters ref="smartFiltersRef" hide-header @apply="$emit('apply')" />
    </div>
  </div>
</template>

<style scoped lang="postcss">
.panel {
  @apply border-b border-tint-80 px-4 py-3 sm:px-8;
}
.bar {
  @apply flex items-center gap-3;
}
.toggle {
  @apply inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] font-bold text-shade-880 transition-colors;
}
.toggle:hover {
  @apply bg-tint-40;
}
.count {
  @apply inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1;
  @apply bg-highlight-500 text-[11px] font-bold text-white;
}
.chevron {
  @apply text-shade-700 transition-transform;
}
.chevron--open {
  @apply rotate-180;
}
.clear {
  @apply rounded-md px-1.5 py-1 text-[11.5px] text-shade-800 transition-colors;
}
.clear:hover {
  @apply bg-tint-40 text-shade-880;
}
.body {
  @apply max-w-[360px] pt-3;
}
</style>
